import { retryWhen, delay, take, tap, finalize, mergeMap } from 'rxjs/operators';
import { timer, throwError, OperatorFunction } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Retry configuration for HTTP requests
 */
export interface RetryConfig {
    maxRetries?: number;
    delayMs?: number;
    logPrefix?: string;
    skipRetryOnStatusCodes?: number[];
    /**
     * Optional callback function triggered on each retry attempt.
     * Receives current attempt number (1-indexed) and max retries.
     */
    onRetry?: (currentAttempt: number, maxRetries: number) => void;
}

/**
 * Default retry configuration: 3 retries with 5-second delays
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    delayMs: 5000,
    logPrefix: '[HTTP]'
};

/**
 * Returns a configured retry operator for HTTP requests
 * Delays only BETWEEN retries, not after the final failure
 * @param config - Optional retry configuration. Uses DEFAULT_RETRY_CONFIG if not provided
 * @returns RxJS pipe operator with retry logic, logging, and error handling
 * 
 * Usage:
 *   return this.httpClient.get(url).pipe(
 *     getHttpRetryOperator()
 *   );
 */
export function getHttpRetryOperator<T>(config: RetryConfig = DEFAULT_RETRY_CONFIG): OperatorFunction<T, T> {
    // Merge with default config to ensure all properties have values
    const mergedConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    const prefix = mergedConfig.logPrefix || '[HTTP]';
    const skipStatusCodes = mergedConfig.skipRetryOnStatusCodes || [];
    const onRetry = mergedConfig.onRetry;
    const maxRetries = mergedConfig.maxRetries;
    const delayMs = mergedConfig.delayMs;

    return retryWhen(errors =>
        errors.pipe(
            mergeMap((error, index) => {
                // Check if this status code should skip retry (but always retry network errors with status 0)
                if (error instanceof HttpErrorResponse && error.status !== 0 && skipStatusCodes.includes(error.status)) {
                    console.log(`${prefix} Request failed with status ${error.status}. Skipping retry (status in skipRetryOnStatusCodes).`, error.message);
                    return throwError(() => error);
                }

                // If we haven't exceeded max retries, delay and retry
                // index 0 = initial failure, will be 1st retry
                // index 1 = 1st retry failed, will be 2nd retry
                // index 2 = 2nd retry failed, will be 3rd retry
                if (index < maxRetries) {
                    const retryNumber = index + 1; // Which retry this will be (1st, 2nd, 3rd)
                    
                    if (index === 0) {
                        console.log(`${prefix} Initial request failed, retrying in ${delayMs}ms...`, error.message);
                    } else {
                        console.log(`${prefix} Retry ${index}/${maxRetries} failed, retrying in ${delayMs}ms...`, error.message);
                    }
                    
                    return timer(delayMs).pipe(
                        tap(() => {
                            // Trigger onRetry callback exactly when retry starts (after delay)
                            if (onRetry) {
                                onRetry(retryNumber, maxRetries);
                            }
                        })
                    );
                }
                // On final failure, throw immediately without delay
                console.log(`${prefix} All ${maxRetries} retries exhausted.`, error.message);
                return throwError(() => error);
            })
        )
    );
}

/**
 * Returns complete pipe operators for HTTP requests with logging
 * @param config - Optional retry configuration. Uses DEFAULT_RETRY_CONFIG if not provided
 * @returns Array of RxJS pipe operators including retry, logging, and finalize
 * 
 * Usage:
 *   return this.httpClient.get(url).pipe(
 *     ...getHttpRetryPipe()
 *   );
 */
export function getHttpRetryPipe(config: RetryConfig = DEFAULT_RETRY_CONFIG) {
    const prefix = config.logPrefix || '[HTTP]';

    return [
        tap(response => {
            console.log(`${prefix} Request successful`, response);
        }),
        getHttpRetryOperator(config),
        finalize(() => {
            console.log(`${prefix} Request attempt completed`);
        })
    ];
}
